import { useEffect, useLayoutEffect, useState } from "react";
// Byte-identical copy of the prototype's <body> scaffold (see the file header).
// Vite's `?raw` import gives us the exact HTML string — no hand-transcription,
// so the DOM/class structure styles.css expects is reproduced perfectly.
import scaffoldHtml from "./legacy-scaffold.html?raw";
import { useProtoStore } from "./store/store.js";
import { installUndo } from "./canvas/undo.js";
import { installCanvas } from "./canvas/shell-actions.js";
import { isMovingModule } from "./canvas/useModuleMove.js";
import { isResizingModule } from "./canvas/useModuleResize.js";
import { installResizeDominance } from "./canvas/resize-dominance.js";
import { installMenuDrag, isDraggingFromMenu } from "./canvas/menu-drag.js";
import { installTopBar } from "./canvas/presets.js";
import { installSidePanel } from "./canvas/side-panel.js";
import { installZoom } from "./canvas/zoom.js";
import { installWelcome } from "./canvas/welcome.js";
import { installTooltip } from "./canvas/tooltip.js";
import { installSelectAllOnClick } from "./canvas/select-all-on-click.js";
import { installStyledSelectOutsideClick } from "./modules/styled-select-menus.js";
import { installModuleConfig, whenModulesReady } from "./modules/module-config.js";
import { installTourApi } from "./canvas/tour-api.js";
import { Canvas } from "./canvas/Canvas.js";
import { installReactModuleBridge } from "./modules/bridge.js";
import { registerReactModules } from "./modules/index.js";
import { versionedUrl } from "./legacy-assets.js";
import { readSnapshot, snapshotMeta } from "./snapshot-loader.js";
import { createBootScreen, type BootScreen } from "./boot-screen.js";
import { appReady, stylesReady } from "./app-ready.js";
import { LIVE } from "./live/flag.js";

// The two legacy scripts left (05k-2), in the order index.html loaded them:
// catalyst-data.js (the snapshot — it sets window.CatalystData; Track 2 retires it) and
// tour.js (kept, decision 4). catalyst-data.js is fetched as its .gz (loadSnapshot below),
// but still runs first. Everything else is TS / React and installs at the old spots of the
// files it replaced: module-loader.js (first), tooltip.js and main.js (before tour.js).
// (The renderer files, table-sort.js and player-utils.js went in 05k-2; js/d3.min.js was
// the npm `d3` since Phase 01.)
const LEGACY_SCRIPTS = ["catalyst-data.js", "tour.js"];

// One object for the page's life, so a re-render of App never resets the scaffold.
const SCAFFOLD = { __html: scaffoldHtml };

// StrictMode double-invokes effects in dev, and the legacy IIFEs are not
// idempotent (they set window globals and boot once). Guard so the mount runs
// exactly once for the page's lifetime.
let booted = false;

// Inject a classic (non-module) <script> so the file's top-level `var`s stay
// global and it runs with the same semantics as the prototype's <script src>.
// `async = false` preserves execution order across sequential appends.
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = false;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load legacy script ${src}`));
    document.body.appendChild(el);
  });
}

// Fetch + decompress the snapshot, then run it as a classic script (via a blob:
// URL) so it behaves exactly like the old <script src="js/catalyst-data.js">.
// The loading screen is fed real bytes received + real unpacked data as it goes.
async function loadSnapshot(boot: BootScreen): Promise<void> {
  const meta = snapshotMeta();
  const blob = await readSnapshot(await fetch(meta.url), meta, {
    onProgress: boot.progress,
    onUnpacked: boot.unpacked,
  });
  await boot.startingUp();
  const url = URL.createObjectURL(blob);
  try {
    await loadScript(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function App() {
  useEffect(() => {
    if (booted) return;
    booted = true;

    const loadingScreen = document.getElementById("loading-screen");
    if (!loadingScreen) throw new Error("#loading-screen missing from legacy-scaffold.html");
    const boot = createBootScreen(loadingScreen);

    void (async () => {
      // styles.css loads without blocking the first paint (so the loading screen
      // shows instantly) — but the legacy code measures the layout, so it waits.
      await stylesReady();
      // Phase 05j-1: modules.json (was module-loader.js, the first legacy script) — its
      // DOMContentLoaded listener stays first in line.
      installModuleConfig();
      for (const name of LEGACY_SCRIPTS) {
        // Where tooltip.js and then main.js loaded (gone since 05j-1 / 05k-1): everything
        // ported from them installs here, in their order, so the page-wide listeners keep
        // the order they had (before tour.js's).
        if (name === "tour.js") {
          // Phase 05j-1: the tooltip — its element on <body> and its page-wide listeners.
          installTooltip();
          // main.js's first line: the page opens at the top, not where a reload left it.
          if ("scrollRestoration" in history) history.scrollRestoration = "manual";
          // Phase 05a: undo lives in the store. Phase 05b: the module shell's actions (and,
          // 05c, the sticky player, the store's).
          const undo = installUndo(useProtoStore);
          installCanvas(useProtoStore, undo.saveState);
          // Phase 05j-1: modules.json's first load starts where main.js started it.
          void whenModulesReady();
          // Phase 05h: drag-to-add — the side panel's tiles + dashboard cards, wired where
          // main.js wired them.
          installMenuDrag();
          // Phase 05i-1: the presets + the top bar's buttons, wired where main.js wired them.
          installTopBar();
          // Phase 05i-2: the side panel, the zoom slider and the welcome screen (+ its ghosts,
          // started on the DOMContentLoaded fired below).
          installSidePanel();
          installZoom();
          installWelcome();
          // Phase 05j-2: search boxes select their text on click (#187), then the styled
          // dropdowns' outside-click close — main.js added both here, in this order.
          installSelectAllOnClick();
          installStyledSelectOutsideClick();
          // Phase 05f: the resize-handle halo's listeners went right AFTER main.js's — so
          // right here. (Every gesture that pauses it is TS and recomputes it at its end.)
          installResizeDominance(() => isMovingModule() || isResizingModule() || isDraggingFromMenu());
          // Phase 05k-1: the tour's small API (decision 4), read by tour.js.
          installTourApi();
        }
        // Live mode never downloads the snapshot (step 1): its widgets read the API only, and
        // data/snapshot.ts throws if anything still asks for it.
        if (name === "catalyst-data.js") {
          if (LIVE) {
            boot.noDownload();
            await boot.startingUp();
          }
          else await loadSnapshot(boot);
        } else await loadScript(versionedUrl(`js/${name}`));
      }
      // Phase 04: register every widget's React module (the shell's refreshModuleBody
      // renders through this registry since 05j-1), and start watching for removed
      // modules to unmount their roots.
      installReactModuleBridge();
      registerReactModules();

      // tour.js inits on DOMContentLoaded, and so do our module-config.ts + welcome.ts (they
      // kept module-loader.js's / main.js's spots). That event fired long before this effect
      // ran, so dispatch a synthetic one now that the scaffold is mounted and every script is
      // loaded — the listeners find the exact DOM they expect. Both targets are used
      // (module-config / tour on document, welcome on window). This is our own controlled
      // page, so re-firing it is safe.
      document.dispatchEvent(new Event("DOMContentLoaded"));
      window.dispatchEvent(new Event("DOMContentLoaded"));

      // Reveal the app only once it's genuinely complete — every script run, AND the
      // widget palette, fonts and first screen ready — so it appears instantly
      // rather than filling in after the fade.
      await appReady();
      boot.ready();
    })().catch((err: unknown) => {
      // Say so on the loading screen (otherwise it just sits there), then rethrow
      // so the real error still reaches the console.
      boot.failed();
      throw err;
    });
  }, []);

  // Phase 05b: the modules are React's, rendered into the scaffold's #workspace. Found
  // before the first paint — and so before any legacy script can create a module.
  const [workspace, setWorkspace] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = document.getElementById("workspace");
    if (!el) throw new Error("#workspace missing from legacy-scaffold.html");
    setWorkspace(el);
  }, []);

  // React owns this container and renders the scaffold once; the legacy JS then
  // takes over imperatively, exactly as it does in the static prototype.
  return (
    <>
      <div id="proto-root" dangerouslySetInnerHTML={SCAFFOLD} />
      {workspace && <Canvas workspace={workspace} />}
    </>
  );
}
